<template>
    <div class="buttons">
        <div class="button-controls d-flex justify-space-between align-center">
            <div class="d-flex ga-2 align-center">
                <v-btn prepend-icon="mdi-printer" @click="printPage">Print</v-btn>
                <v-btn prepend-icon="mdi-download" :disabled="!data || loading" @click="downloadCsv">Download</v-btn>
            </div>
            <div class="d-flex ga-2 align-center">
                <v-text-field
                    v-model="startDate"
                    label="Start Date"
                    type="date"
                    density="compact"
                    hide-details
                    :max="maxStartDate"
                    style="width: 170px;"
                ></v-text-field>
                <v-text-field
                    v-model="endDate"
                    label="End Date"
                    type="date"
                    density="compact"
                    hide-details
                    :min="startDate"
                    :max="maxEndDate"
                    style="width: 170px;"
                ></v-text-field>
            </div>
        </div>
    </div>
    <div class="book">
        <v-skeleton-loader v-if="loading" type="table"></v-skeleton-loader>
        <div v-if="!loading && data">
            <div class="page" v-for="page in data" :key="page.line">
                <div class="subpage">
                    <h2 class="report-header">{{ page.header }}</h2>
                    <table class="job-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Start Time</th>
                                <th>End Time</th>
                                <th>Line Time</th>
                                <th>Estimated Time</th>
                                <th>Contact</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in page.rows" :key="index">
                                <td>{{ row.date ? $dayjs(row.date).format('DD/MM/YYYY') : '' }}</td>
                                <td>{{ row.type }}</td>
                                <td>{{ row.description }}</td>
                                <td>{{ row.startTime ? $dayjs(row.startTime).format('HH:mm') : '' }}</td>
                                <td>{{ row.endTime ? $dayjs(row.endTime).format('HH:mm') : '' }}</td>
                                <td>{{ formatMinutes(row.lineTime) }}</td>
                                <td>{{ formatMinutes(row.estimatedTime) }}</td>
                                <td>{{ row.contact }}</td>
                            </tr>
                            <tr v-if="!page.rows.length">
                                <td colspan="8" style="text-align: center;">No entries for this period.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import { mapActions, mapState } from "vuex";
    import { getTagClass } from '../mixins/jobMixins';
    import { numberWithCommas } from "../mixins/numberMixins";
    function initialState() {
        return {
            saving: false,
            loading: false,
            data: null,
            startDate: null,
            endDate: null
        };
    }
    export default {
        name: "ProductionTimeView",
        data: function () {
            return initialState();
        },
        mounted: function () {
            const today = this.$dayjs();
            const dayOfWeek = today.day(); // 0=Sun, 1=Mon ... 6=Sat
            // Find the most recent Monday (previous week's Monday)
            const daysToLastMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            const lastMonday = today.subtract(daysToLastMonday + 7, 'day');
            const lastFriday = lastMonday.add(4, 'day');
            this.startDate = lastMonday.format('YYYY-MM-DD');
            this.endDate = lastFriday.format('YYYY-MM-DD');
            this.loadData();
        },
        props: {
        },
        methods: {
            ...mapActions([
                "showErrorSnack"
            ]),
            getTagClass,
            numberWithCommas,
            reset: function () {
                Object.assign(this.$data, initialState());
            },
            formatMinutes(mins) {
                if (mins === null || mins === undefined) return '';
                const total = Math.round(mins);
                const h = Math.floor(total / 60);
                const m = total % 60;
                return `${h}:${m.toString().padStart(2, '0')}`;
            },
            downloadCsv() {
                if (!this.data) return;
                const headers = ['Line', 'Date', 'Type', 'Description', 'Start Time', 'End Time', 'Line Time', 'Estimated Time', 'Contact'];
                const escape = (val) => {
                    const s = val === null || val === undefined ? '' : String(val);
                    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                };
                const rows = [headers.join(',')];
                this.data.forEach(page => {
                    page.rows.forEach(row => {
                        rows.push([
                            `Line ${page.line}`,
                            row.date ? this.$dayjs(row.date).format('DD/MM/YYYY') : '',
                            row.type,
                            row.description,
                            row.startTime ? this.$dayjs(row.startTime).format('HH:mm') : '',
                            row.endTime ? this.$dayjs(row.endTime).format('HH:mm') : '',
                            this.formatMinutes(row.lineTime),
                            this.formatMinutes(row.estimatedTime),
                            row.contact
                        ].map(escape).join(','));
                    });
                });
                const blob = new Blob(['﻿' + rows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `production-time_${this.startDate}_${this.endDate}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            },
            printPage() {
                var css = '@page { size: A4 landscape!important; }',
                    head = document.head || document.getElementsByTagName('head')[0],
                    style = document.createElement('style');

                style.type = 'text/css';
                style.media = 'print';

                if (style.styleSheet){
                  style.styleSheet.cssText = css;
                } else {
                  style.appendChild(document.createTextNode(css));
                }

                head.appendChild(style);
                window.print();
            },
            loadData() {
                if (!this.startDate || !this.endDate) return;
                this.loading = true;
                this.$api.get(`report/productiontime?startDate=${this.startDate}&endDate=${this.endDate}`)
                    .then(res => {
                        this.data = res.data;
                        this.loading = false;
                    })
                    .catch(error => {
                        this.loading = false;
                        this.showErrorSnack('There was an issue getting the report');
                    });
            },
        },
        computed: {
            maxStartDate() {
                if (this.endDate) {
                    return this.$dayjs(this.endDate).subtract(0, 'day').format('YYYY-MM-DD');
                }
                return null;
            },
            maxEndDate() {
                if (this.startDate) {
                    return this.$dayjs(this.startDate).add(14, 'day').format('YYYY-MM-DD');
                }
                return null;
            }
        },
        watch: {
            startDate: {
                handler(newVal, oldVal) {
                    if (oldVal === null) return;
                    // If end date is beyond 14 days of new start, clamp it
                    if (this.endDate && this.$dayjs(this.endDate).diff(this.$dayjs(newVal), 'day') > 14) {
                        this.endDate = this.$dayjs(newVal).add(14, 'day').format('YYYY-MM-DD');
                    }
                    this.loadData();
                }
            },
            endDate: {
                handler(newVal, oldVal) {
                    if (oldVal === null) return;
                    this.loadData();
                }
            }
        }
    };
</script>
<style scoped lang="scss">
    .overflow-stock td {
        background-color: #3f0000 !important;
        color: white !important;
    }
    .buttons {
        width: 100%;
        position: fixed;
        top: 0;
        background-color: #FAFAFA;
        border-bottom: 1px #D3D3D3 solid;
        z-index: 10;
    }

    .button-controls {
        padding-top: 20px;
        width: 287mm;
        margin-left: auto;
        margin-right: auto;
        padding-bottom: 20px;
    }

    :deep(.v-textarea textarea) {
        font-size: 12px !important;
    }

    .report-header {
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 4px;
    }

    .job-table {
        border-collapse: collapse;
        width: 100%;
        margin-top: 10px;
        border: none !important;
    }

    .job-table, .job-table td, th {
        border: 1px #D3D3D3 solid;
        font-size: 12px;
        padding: 3px;
    }

    body {
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        background-color: #FAFAFA;
        font: 12pt "Tahoma";
    }

    .name-row td, th {
        box-shadow: inset -1px -1px 100px 100px #212121;
        font-weight: bold;
        color: white;
    }

    * {
        box-sizing: border-box;
        -moz-box-sizing: border-box;
    }

    .book {
        margin-top: 80px;
    }

    .page {
        width: 287mm;
        min-height: 210mm;
        padding: 10mm;
        margin: 10mm auto;
        border: 1px #D3D3D3 solid;
        border-radius: 5px;
        background: white;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    }

    .subpage {
        padding: 1cm;
        width: 257mm;
        height:auto;
    }
    .footer-repeat, .header-repeat {
        display:none;
    }

    @page {
        size: A4;
        margin: 0;
    }

    @media print {
        .subpage {
            page-break-before: auto;
        }

        .no-border {
            border: none !important;
            height:10mm;
        }

        .footer-repeat {
            display: table-footer-group;
            border: none !important;
        }

        .header-repeat {
            display: table-header-group;
            border: none !important;
        }

        .bg-orange-lighten-4 {
            box-shadow: inset -1px -1px 100px 100px #ffe0b2;
        }

        .bg-orange-darken-1 {
            box-shadow: inset -1px -1px 100px 100px #fb8c00;
        }

        .bg-red-lighten-4 {
            box-shadow: inset -1px -1px 100px 100px #FFCDD2;
        }

        .bg-red-darken-1 {
            box-shadow: inset -1px -1px 100px 100px #E53935;
        }

        @page {
            size: landscape A4;
        }

        html, body {
            height: 210mm;
            width: 297mm;
        }

        .book {
            margin-top: 0px;
        }

        .buttons {
            display: none;
        }

        :deep(.v-textarea textarea) {
            overflow: hidden;
        }

        .page {
            margin: 0;
            border: initial;
            border-radius: initial;
            width: initial;
            min-height: initial;
            box-shadow: initial;
            background: initial;
            page-break-after: always;
        }
    }
</style>